import { useEffect, useState } from "react";
import { X, MapPin, ChevronDown, ArrowLeft, Video } from "lucide-react";
import { api } from "../../lib/api";

const TABS = [
    { id: "for_you",  label: "For You" },
    { id: "nearby",   label: "Nearby" },
    { id: "new",      label: "New" },
    { id: "trending", label: "Trending" },
];

const CATEGORIES = [
    { id: "flats",         label: "Flats" },
    { id: "houses",        label: "Houses" },
    { id: "plots",         label: "Plots" },
    { id: "commercial",    label: "Commercial" },
    { id: "new_projects",  label: "New Projects" },
];

export default function ReelTabs({ tab, setTab, city, setCity, category, setCategory, onClose }) {
    const [cities, setCities] = useState([]);
    const [showCityPicker, setShowCityPicker] = useState(false);

    useEffect(() => {
        api.get("/reels/cities").then((r) => setCities(r.data?.items || [])).catch(() => setCities([]));
    }, []);

    return (
        <>
            <div className="absolute top-0 inset-x-0 z-20 pt-3 pb-2 px-3" data-testid="reel-tabs-bar">
                {/* Title row */}
                <div className="flex items-center gap-2 mb-2">
                    <button
                        aria-label="Back"
                        onClick={onClose}
                        data-testid="reels-close"
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0"
                        style={{ background: "rgba(0,0,0,0.45)" }}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1 flex items-center gap-1.5 min-w-0">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                             style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                            <Video className="w-3.5 h-3.5" style={{ color: "#001a3d" }} />
                        </div>
                        <div className="font-heading font-extrabold text-white text-[14px] drop-shadow" data-testid="reels-title">
                            Property Reels
                        </div>
                    </div>
                    <button
                        onClick={() => setShowCityPicker(true)}
                        data-testid="reels-city-picker"
                        className="inline-flex items-center gap-1 text-white text-[11px] font-bold px-2.5 h-9 rounded-full shrink-0"
                        style={{ background: "rgba(0,0,0,0.45)" }}
                    >
                        <MapPin className="w-3 h-3" />
                        <span className="max-w-[90px] truncate">{city || "All Cities"}</span>
                        <ChevronDown className="w-3 h-3" />
                    </button>
                </div>
                {/* Tabs row */}
                <div className="overflow-x-auto no-scrollbar">
                    <div className="flex gap-1.5 pr-4">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`reels-tab-${t.id}`}
                                className={`px-3 h-8 rounded-full text-[11.5px] font-extrabold whitespace-nowrap transition-colors ${
                                    tab === t.id ? "text-[#001a3d]" : "text-white"
                                }`}
                                style={{
                                    background: tab === t.id
                                        ? "linear-gradient(90deg, #DAA520, #F59E0B)"
                                        : "rgba(0,0,0,0.45)",
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
                {/* Category strip */}
                <div className="mt-2 overflow-x-auto no-scrollbar">
                    <div className="flex gap-1.5 pr-4">
                        <Chip active={!category} onClick={() => setCategory(null)} testid="reels-cat-all">All</Chip>
                        {CATEGORIES.map((c) => (
                            <Chip
                                key={c.id}
                                active={category === c.id}
                                onClick={() => setCategory(category === c.id ? null : c.id)}
                                testid={`reels-cat-${c.id}`}
                            >
                                {c.label}
                            </Chip>
                        ))}
                    </div>
                </div>
            </div>

            {showCityPicker && (
                <div className="absolute inset-0 z-30 flex items-end" style={{ background: "rgba(0,0,0,0.6)" }}
                     onClick={() => setShowCityPicker(false)} data-testid="reels-city-sheet">
                    <div className="w-full bg-white rounded-t-2xl p-4 max-h-[65vh] overflow-y-auto"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Choose City</div>
                            <button onClick={() => setShowCityPicker(false)} className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ background: "#f4f4f5" }} data-testid="reels-city-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <button
                            onClick={() => { setCity(null); setShowCityPicker(false); }}
                            data-testid="reels-city-all"
                            className="w-full text-left px-3 py-2.5 rounded-lg text-[12.5px] font-bold border border-transparent hover:border-navy-500/10"
                            style={{ color: !city ? "#F59E0B" : "#002657" }}
                        >
                            All Cities
                        </button>
                        <div className="mt-1 divide-y divide-navy-500/5">
                            {cities.length === 0 && (
                                <div className="text-[11.5px] text-navy-500/60 py-4 text-center">No cities yet — be the first to publish a reel in your city.</div>
                            )}
                            {cities.map((c) => (
                                <button
                                    key={c.city}
                                    onClick={() => { setCity(c.city); setShowCityPicker(false); }}
                                    data-testid={`reels-city-${c.city}`}
                                    className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                                    style={{ color: city === c.city ? "#F59E0B" : "#002657" }}
                                >
                                    <span className="text-[12.5px] font-bold">{c.city}</span>
                                    <span className="text-[10.5px] text-navy-500/50">{c.count} reels</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Chip({ children, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-2.5 h-7 rounded-full text-[11px] font-bold whitespace-nowrap ${
                active ? "text-white" : "text-white/80"
            }`}
            style={{
                background: active ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.4)",
                border: active ? "1px solid rgba(245,229,165,0.7)" : "1px solid rgba(255,255,255,0.15)",
            }}
        >
            {children}
        </button>
    );
}
